import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { leaveApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

interface PolicyFormData {
  name: string;
  leave_type: string;
  days_per_year: number;
  carry_forward_days: number;
  is_active: boolean;
}

const LeavePolicies: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<any>(null);
  const [formData, setFormData] = useState<PolicyFormData>({
    name: '',
    leave_type: '',
    days_per_year: 0,
    carry_forward_days: 0,
    is_active: true,
  });

  // Fetch policies
  const { data: policies = [], isLoading, error } = useQuery({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: leaveApi.policies.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leavePolicies'] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: 'Leave policy created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => leaveApi.policies.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leavePolicies'] });
      setIsDialogOpen(false);
      setEditingPolicy(null);
      resetForm();
      toast({ title: 'Leave policy updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (!formData.name || !formData.leave_type) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    if (editingPolicy) {
      updateMutation.mutate({ id: editingPolicy.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const openDialog = (policy?: any) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        name: policy.name,
        leave_type: policy.leave_type,
        days_per_year: policy.days_per_year,
        carry_forward_days: policy.carry_forward_days || 0,
        is_active: policy.is_active,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      leave_type: '',
      days_per_year: 0,
      carry_forward_days: 0,
      is_active: true,
    });
    setEditingPolicy(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading policies...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading policies</p>
        <p className="text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configure leave policies and allowances</h1>
          
        </div>
        <Button className='flex items-center  bg-[#1e3a5f] hover:bg-[#143A62] text-white m-2' onClick={() => openDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Policy
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Leave Policies</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy Name</TableHead>
                <TableHead>Leave Type</TableHead>
                <TableHead>Days Per Year</TableHead>
                <TableHead>Carry Forward</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No policies found. Create your first leave policy.
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy: any) => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-medium">{policy.name}</TableCell>
                    <TableCell>{policy.leave_type}</TableCell>
                    <TableCell>{policy.days_per_year} days</TableCell>
                    <TableCell>{policy.carry_forward_days || 0} days</TableCell>
                    <TableCell>
                      <Badge variant={policy.is_active ? 'default' : 'secondary'}>
                        {policy.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDialog(policy)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create Policy'}</DialogTitle>
            <DialogDescription>
              Configure leave policy details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Policy Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Annual Leave"
              />
            </div>
            <div>
              <Label htmlFor="leave_type">Leave Type *</Label>
              <Input
                id="leave_type"
                value={formData.leave_type}
                onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                placeholder="e.g., ANNUAL, SICK"
              />
            </div>
            <div>
              <Label htmlFor="days_per_year">Days Per Year *</Label>
              <Input
                id="days_per_year"
                type="number"
                value={formData.days_per_year}
                onChange={(e) => setFormData({ ...formData, days_per_year: parseFloat(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="carry_forward_days">Carry Forward Days</Label>
              <Input
                id="carry_forward_days"
                type="number"
                value={formData.carry_forward_days}
                onChange={(e) => setFormData({ ...formData, carry_forward_days: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingPolicy ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavePolicies;